import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import axios from "axios";
import posthog from "posthog-js";
import { generateApiKey } from "../../../../helpers/common.helpers";
import "./SectionOne.css";

// Move RESOURCES outside component to prevent recreation
// Custom Card Components to replace Shadcn Card
const Card = ({ children, className = "" }) => {
  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white text-gray-950 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const CardContent = ({ children, className = "" }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>;
};

const CardHeader = ({ children, className = "" }) => {
  return <div className={`flex space-y-1.5 p-6 ${className}`}>{children}</div>;
};

const CardTitle = ({ children, className = "" }) => {
  return (
    <h3
      className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
    >
      {children}
    </h3>
  );
};

// Custom Button Component to replace Shadcn Button
const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

  const variantClasses = {
    default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    outline:
      "border border-gray-300 bg-transparent hover:bg-gray-50 focus:ring-gray-300",
    ghost: "hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-300",
  };

  const sizeClasses = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3 text-sm",
    lg: "h-11 rounded-md px-8",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Custom Progress Component to replace Shadcn Progress
const Progress = ({ value, className = "" }) => {
  return (
    <div
      className={`relative h-2 w-full overflow-hidden rounded-full bg-blue-100 ${className}`}
    >
      <div
        className="h-full w-full flex-1 bg-blue-600 transition-all duration-300 ease-in-out"
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </div>
  );
};

// Custom Badge Component to replace Shadcn Badge
const Badge = ({ children, className = "", variant = "default" }) => {
  const variantClasses = {
    default: "bg-blue-600 text-white",
    secondary: "bg-gray-100 text-gray-800",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
};

const Curriculum = () => {
  // State for API data
  const [courseDetails, setCourseDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  // Attendance state and modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [table, setTable] = useState([]);
  const [showAttendance, setShowAttendance] = useState(false);
  const [attendanceType, setAttendanceType] = useState("offline");
  const [isAttendanceActivated, setIsAttendanceActivated] = useState(false);

  const iitkCateoryMap = [26, 38, 49]

  // API Integration - Fetch course details
  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      const CourseData = {
        enrol_id: localStorage.getItem("enrol_id"),
      };

      const options = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/dashboardhomepage/fetchcoursedetails`,
        JSON.stringify(CourseData),
        options
      );

      localStorage.setItem("courseDetails", JSON.stringify(response.data));
      setCourseDetails(response.data);

      // PostHog: capture dashboard_viewed + milestone tracking
      try {
        const data = response.data;
        if (data?.terms) {
          const totalTerms = data.terms.length;
          const completedTerms = (data.active_term ?? 1) - 1;
          const progress_percentage = totalTerms > 0
            ? Math.round((completedTerms / totalTerms) * 100)
            : 0;

          localStorage.setItem("progress_percentage", progress_percentage);
          window.dispatchEvent(new Event("storage"));

          const current_program = localStorage.getItem("program_name");

          posthog.capture('dashboard_viewed', {
            current_program,
            progress_percentage,
          });

          // Milestone tracking — only fire once per enrollment per threshold
          const milestones = [60, 70, 75, 85, 100];
          const enrolId = localStorage.getItem("enrol_id") || "unknown";

          milestones.forEach((level) => {
            const milestoneKey = `milestone_${enrolId}_${level}`;
            if (progress_percentage >= level && !localStorage.getItem(milestoneKey)) {
              posthog.capture('program_progress_milestone', {
                milestone: level,
                current_program,
              });
              localStorage.setItem(milestoneKey, true);
            }
          });
        }
      } catch (phErr) {
        console.error("PostHog capture error:", phErr);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching course details:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseDetails();
  }, []);

  // Calculate dynamic data based on API response
  const getDynamicData = () => {
    if (!courseDetails || !courseDetails.terms) {
      // Return default static data if API data not available
      return {
        currentProgram: {
          title: "Executive Program in Digital Transformation & Innovation",
          progress: 75,
          status: "Active",
          timeSpent: "24.5 hours",
          nextDeadline: "Assignment due in 3 days",
        },
        historicalPrograms: [
          {
            title: "Digital Business Models and Value Proposition",
            progress: 100,
            status: "Completed",
            completedDate: "Oct 2024",
          },
          {
            title: "Digital Technology & Tools",
            progress: 60,
            status: "Ongoing",
            completedDate: null,
          },
          {
            title: "Digital Strategy & Planning",
            progress: 100,
            status: "Not Started",
            completedDate: "Nov 2024",
          },
          {
            title: "Digital Business Models and Value Proposition",
            progress: 100,
            status: "Completed",
            completedDate: "Oct 2024",
          },
          {
            title: "Digital Strategy & Planning",
            progress: 100,
            status: "Completed",
            completedDate: "Nov 2024",
          },
          {
            title: "Digital Business Models and Value Proposition",
            progress: 100,
            status: "Completed",
            completedDate: "Oct 2024",
          },
          {
            title: "Digital Business Models and Value Proposition",
            progress: 100,
            status: "Completed",
            completedDate: "Oct 2024",
          },
        ],
      };
    }

    // Calculate progress based on API data
    const { program_code, active_term, terms, specialization } = courseDetails;
    const totalTerms = terms.length;
    const completedTerms = active_term - 1;
    const progressPercentage = Math.round((completedTerms / totalTerms) * 100);

    // Find current active term
    const activeTermData = terms.find((term) => term.term_id === active_term);

    // Create current program object
    const currentProgram = {
      title: program_code + (specialization ? ` - ${specialization}` : ""),
      progress: progressPercentage,
      status: "Active",
      timeSpent: "24.5 hours", // Keep static or make dynamic as needed
      nextDeadline: "Assignment due in 3 days", // Keep static or make dynamic as needed
    };

    // Process all terms with their statuses
    const historicalPrograms = terms.map((term) => {
      let status, progress;

      if (term.term_id < active_term) {
        status = "Completed";
        progress = 100;
      } else if (term.term_id === active_term) {
        status = "Ongoing";
        progress = 75; // You can make this dynamic based on actual progress
      } else {
        status = "Not Started";
        progress = 0;
      }

      return {
        title: term.term_name,
        progress,
        status,
        completedDate: status === "Completed" ? "Completed" : null,
      };
    });

    return { currentProgram, historicalPrograms };
  };

  const { currentProgram, historicalPrograms } = getDynamicData();

  // Enhanced Ongoing Indicator Component for main current program
  const OngoingIndicator = () => (
    <div className="relative inline-flex items-center justify-center">
      {/* Outer pulsing ring */}
      <div className="absolute w-4 h-4 bg-blue-500/20 rounded-full animate-ping" />
      {/* Middle ring */}
      <div className="absolute w-3 h-3 bg-blue-500/40 rounded-full animate-pulse" />
      {/* Inner solid dot */}
      <div className="w-2 h-2 bg-blue-500 rounded-full relative z-10" />
      {/* Rotating border */}
      <div className="absolute w-4 h-4 border-2 border-transparent border-t-blue-500 rounded-full animate-spin" />
    </div>
  );

  // New subtle animation for ongoing programs in the list
  const OngoingListIndicator = () => (
    <div className="relative inline-flex items-center justify-center">
      {/* Breathing outer glow */}
      <div className="absolute w-3 h-3 bg-gradient-to-r from-blue-400 to-blue-400 rounded-full animate-breathing opacity-30" />
      {/* Shimmer effect */}
      <div className="absolute w-2.5 h-2.5 bg-gradient-to-r from-blue-500 to-blue-500 rounded-full animate-shimmer-glow" />
      {/* Core dot */}
      <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-blue-500 rounded-full relative z-10 animate-heartbeat" />
    </div>
  );

  return (
    <>
      <Card
        className={`lg:col-span-2 h-[30rem] flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm`}
      >
        {/* Learning Progress Card Header */}
        <CardHeader className="flex-shrink-0 pb-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600/20 backdrop-blur-sm border border-blue-indigo/30 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-medium text-gray-900">
                Learning Progress
              </CardTitle>
            </div>
          </div>

          {/* Attendance Button, aligned right */}
        </CardHeader>

        <CardContent className="flex-1 overflow-y-hidden">
          {/* Current Program */}
          <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <OngoingIndicator />
                <span className="font-medium text-gray-900">
                  {localStorage.getItem("program_name") && iitkCateoryMap.includes(Number(localStorage.getItem("category"))) ? 'E&ICT Academy ' + localStorage.getItem("program_name") : localStorage.getItem("program_name") || currentProgram.title}
                </span>
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                  {currentProgram.status}
                </Badge>
              </div>
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Progress</span>
                <span className="font-medium text-gray-900">
                  {currentProgram.progress}%
                </span>
              </div>
              <Progress
                value={currentProgram.progress}
                className="h-1 bg-blue-100 animated-progress"
              />
            </div>
          </div>

          {/* All Programs */}
          <div className="">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">All Terms</h3>
            </div>
            <div className="overflow-y-auto h-[200px] custom-scrollbar">
              <div className="space-y-3">
                {historicalPrograms.map((program, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {program.status === "Ongoing" ? (
                          <OngoingListIndicator />
                        ) : program.status === "Completed" ? (
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                        ) : (
                          <div className="w-2 h-2 bg-gray-400 rounded-full" />
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {program.title}
                        </span>
                      </div>
                    </div>

                    <div className="ml-4 text-right">
                      <Badge
                        className={`text-xs mb-1 ${program.status === "Ongoing"
                          ? "bg-blue-600 text-white"
                          : program.status === "Completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                          }`}
                      >
                        {program.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default Curriculum;
